import React from "react";
import MenuItem from "@mui/material/MenuItem";
import { REACT_APP_COGNITO_AUTHORITY, REACT_APP_COGNITO_CLIENT_ID, REACT_APP_HOST, REACT_APP_COGNITO_DOMAIN } from '../../Config';

/**
 * A component that renders a single menu item for the header menu. It handles actions such as opening URLs
 * in a new tab or signing out the user. The component is designed to work within a dropdown menu.
 * 
 * @component
 * @example
 * import MenuListHeader from "../MenuListHeader/MenuListHeader";
 * <MenuListHeader item={menuItem} afterClick={handleAfterClick} />
 * @param {Object} item - The menu item object containing the label, icon, action, and value.
 * @param {string} item.label - The display text for the menu item.
 * @param {React.Element} item.icon - The icon to display alongside the label.
 * @param {string} item.action - The action to perform when the item is clicked (e.g., "URL", "sign_out").
 * @param {string} item.value - The value associated with the action (e.g., a URL for "URL" action).
 * @param {function} afterClick - A callback function to execute after the menu item is clicked.
 * @returns {React.Fragment} The rendered menu item component.
 */
function MenuListHeader({ item, afterClick }) {

  /**
   * Handles the sign-out process by redirecting the user to the Cognito logout endpoint.
   */
  const signOutRedirect = () => {
    const authority = REACT_APP_COGNITO_AUTHORITY;
    const clientId = REACT_APP_COGNITO_CLIENT_ID;
    const logoutUri = `${REACT_APP_HOST}/logout`;
    const cognitoDomain = REACT_APP_COGNITO_DOMAIN;
    sessionStorage.removeItem(`oidc.user:${authority}:${clientId}`);
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };
  
  /**
   * Handles the click event for the menu item. Performs the action specified in the item (e.g., opening a URL or signing out).
   * 
   * @param {Object} item - The menu item object containing the action and value.
   */
  const onClickOptionHandler = (item) => {
    if (item.action === "URL") {
      window.open(item.value, "_blank");
    } else if (item.action === "sign_out") {
      signOutRedirect();
    }
    afterClick && afterClick();
  };

  return (
    <MenuItem onClick={onClickOptionHandler.bind(null, item)}>
      {item.icon}{item.label}
    </MenuItem>
  );
}

export default MenuListHeader;